/**
 * Post hub AJAX filtering — shared by the /resources/ and /news/ hubs.
 *
 * Progressive enhancement over the server-rendered filter/pagination links:
 * intercepts clicks, fetches the target URL, and swaps in the fresh filter
 * card and results grid from the response. URLs stay shareable via the
 * History API, and everything still works as plain links without JS.
 */
document.addEventListener('DOMContentLoaded', () => {
	const root = document.querySelector('[data-post-hub]')

	if (!root) {
		return
	}

	const FILTERS = '[data-post-hub-filters]'
	const RESULTS = '[data-post-hub-results]'
	let controller = null

	/**
	 * Dim the results region and mark it busy while a swap is in flight.
	 *
	 * @param {boolean} busy Whether a fetch is in progress.
	 * @return {void}
	 */
	const setBusy = (busy) => {
		const results = root.querySelector(RESULTS)

		if (!results) {
			return
		}

		results.setAttribute('aria-busy', busy ? 'true' : 'false')
		results.style.opacity = busy ? '0.5' : ''
		results.style.pointerEvents = busy ? 'none' : ''
	}

	/**
	 * Fetch a hub URL and swap the current filter card and results grid
	 * for the ones in the response. Falls back to a full page navigation
	 * when the fetch fails or the response is missing either fragment.
	 *
	 * @param {string}  url                             Hub URL to load.
	 * @param {Object}  [options]                       Behavior flags.
	 * @param {boolean} [options.push=true]             Push the URL onto the history stack.
	 * @param {boolean} [options.scrollToResults=false] Scroll the results into view after the swap.
	 * @return {Promise<void>} Resolves once the swap (or fallback) has run.
	 */
	const load = async (url, { push = true, scrollToResults = false } = {}) => {
		if (controller) {
			controller.abort()
		}

		controller = new AbortController()
		setBusy(true)

		let doc

		try {
			const response = await fetch(url, { signal: controller.signal })

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`)
			}

			doc = new DOMParser().parseFromString(await response.text(), 'text/html')
		} catch (error) {
			if (error.name === 'AbortError') {
				return
			}

			window.location.assign(url)
			return
		}

		const newResults = doc.querySelector(RESULTS)
		const results = root.querySelector(RESULTS)

		if (!newResults || !results) {
			window.location.assign(url)
			return
		}

		const newFilters = doc.querySelector(FILTERS)
		const filters = root.querySelector(FILTERS)

		// The filter card is absent until a hub has categories, and the swap
		// must still work in that state — only replace it when both sides have one.
		if (newFilters && filters) {
			filters.replaceWith(newFilters)
		}

		// Swap children, not the node: the results wrapper hosts aria-live,
		// which only announces reliably when the live region itself survives.
		results.replaceChildren(...newResults.childNodes)
		document.title = doc.title
		setBusy(false)

		if (push) {
			window.history.pushState({ postHub: true }, '', url)
		}

		// The clicked link was destroyed by the swap; give keyboard users a
		// place to continue from instead of dropping focus to <body>.
		results.setAttribute('tabindex', '-1')
		results.focus({ preventScroll: true })

		if (scrollToResults) {
			results.scrollIntoView({ behavior: 'smooth', block: 'start' })
		}
	}

	root.addEventListener('click', (event) => {
		// Leave modified clicks (new tab/window) to the browser.
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
			return
		}

		const link = event.target.closest(`${FILTERS} a, nav.pagination a, a[data-post-hub-link]`)

		if (!link || !root.contains(link) || link.origin !== window.location.origin) {
			return
		}

		event.preventDefault()
		load(link.href, { scrollToResults: link.closest('nav.pagination') !== null })
	})

	window.addEventListener('popstate', () => {
		load(window.location.href, { push: false })
	})
})
